import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Which of the three interfaces is showing.
 *
 *   v1  Classic  — the original dashboard. Base stylesheet, no effects.
 *   v2  Console  — SAME DOM as v1, restyled entirely through
 *                  [data-theme="v2"] rules, plus the ambient canvas layers.
 *   v3  Jarvis   — its own shell, its own nav, its own views.
 *
 * The only thing this writes is `data-theme` on <html>, exactly as the old
 * haxThemeToggle() did, so all 900+ lines of the existing stylesheet keep
 * working untouched. Components read `theme` to pick a shell or a variant;
 * they must NOT branch on it for styling — that is the cascade's job.
 *
 * Storage keys (af_theme, af_boot_enabled, af_v3_seen) are unchanged, so a
 * user's preference carries across from the old page.
 */

const ThemeContext = createContext(null);

export const THEMES = ['v1', 'v2', 'v3'];
const DEFAULT_THEME = 'v3';

const read = (k, fallback) => { try { return localStorage.getItem(k) ?? fallback; } catch { return fallback; } };
const write = (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } };

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    const stored = read('af_theme', DEFAULT_THEME);
    return THEMES.includes(stored) ? stored : DEFAULT_THEME;
  });
  // The original stored this as the strings 'true'/'false'. Reading it as
  // !== '0' would have read a stored 'false' as ON, silently switching the boot
  // sequence back on for everyone who had turned it off. Both spellings are
  // accepted on the way in; 'true'/'false' is what goes back out, so a rollback
  // to the old build still reads the right value.
  const [bootEnabled, setBootEnabled] = useState(() => {
    const stored = read('af_boot_enabled', null);
    if (stored === null) return true;
    return stored !== 'false' && stored !== '0';
  });
  const [onboardingSeen, setOnboardingSeen] = useState(() => Boolean(read('af_v3_seen', '')));

  // The single source of truth for every themed rule in the stylesheet.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const setTheme = useCallback(next => {
    if (!THEMES.includes(next)) return;
    write('af_theme', next);
    setThemeState(next);
  }, []);

  const cycleTheme = useCallback(() => {
    setThemeState(prev => {
      const next = THEMES[(THEMES.indexOf(prev) + 1) % THEMES.length];
      write('af_theme', next);
      return next;
    });
  }, []);

  const toggleBoot = useCallback(() => {
    setBootEnabled(prev => { write('af_boot_enabled', prev ? 'false' : 'true'); return !prev; });
  }, []);

  // The guide is open right now. Separate from `onboardingSeen`, which records
  // that it has been shown once — the "?" button reopens it afterwards.
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const openOnboarding = useCallback(() => setOnboardingOpen(true), []);

  const dismissOnboarding = useCallback(() => {
    write('af_v3_seen', '1');
    setOnboardingSeen(true);
    setOnboardingOpen(false);
  }, []);

  // Whether the boot overlay is on screen right now. It lives here because the
  // overlay and the "Replay boot" button are rendered by sibling components.
  const [bootRunning, setBootRunning] = useState(false);
  const startBoot = useCallback(() => setBootRunning(true), []);
  const endBoot = useCallback(() => setBootRunning(false), []);

  const value = useMemo(() => ({
    theme, setTheme, cycleTheme,
    bootEnabled, toggleBoot,
    bootRunning, startBoot, endBoot,
    onboardingSeen, onboardingOpen, openOnboarding, dismissOnboarding,
    isV3: theme === 'v3',
    // v1 and v2 share a DOM and a shell; only the paint differs.
    isClassic: theme === 'v1' || theme === 'v2',
  }), [theme, setTheme, cycleTheme, bootEnabled, toggleBoot,
       bootRunning, startBoot, endBoot,
       onboardingSeen, onboardingOpen, openOnboarding, dismissOnboarding]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
